/**
 * ImageCard Component
 *
 * Individual image card with checkbox selection and hover overlay with actions.
 * Actions: View (eye), Download, Edit (wand), Delete (red).
 */

import { useState } from 'react';
import { Eye, Download, Wand2, Trash2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IImageCardProps } from '@/@types/avatars';

/**
 * Get Tailwind aspect ratio class based on aspect ratio value
 */
const getAspectRatioClass = (ratio?: string): string => {
  switch (ratio) {
    case '1:1':
      return 'aspect-square';
    case '16:9':
      return 'aspect-video';
    case '4:5':
      return 'aspect-[4/5]';
    case '9:16':
    default:
      return 'aspect-[9/16]';
  }
};

export const ImageCard = ({
  image,
  isSelected,
  onToggleSelect,
  onView,
  onDownload,
  onEdit,
  onDelete,
  aspectRatio,
}: IImageCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const showOverlay = isHovered || isSelected;

  // Use aspectRatio from prop or fall back to image's aspectRatio
  const effectiveRatio = aspectRatio || image.aspectRatio;
  const aspectClass = getAspectRatioClass(effectiveRatio);

  return (
    <div
      className={cn(
        'relative w-full rounded-xl overflow-hidden cursor-pointer transition-all',
        aspectClass,
        isSelected
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
          : 'border border-border hover:border-muted-foreground/50'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      <img
        src={image.url}
        alt="Generated avatar"
        className="w-full h-full object-cover"
      />

      {/* Checkbox (top-left) */}
      <button
        onClick={e => {
          e.stopPropagation();
          onToggleSelect();
        }}
        className={cn(
          'absolute top-3 left-3 w-6 h-6 rounded-md flex items-center justify-center transition-all',
          isSelected
            ? 'bg-primary text-primary-foreground'
            : 'bg-black/40 hover:bg-black/60 text-white'
        )}
      >
        {isSelected && <Check className="w-4 h-4" />}
      </button>

      {/* Action Overlay (bottom, on hover/selected) */}
      {showOverlay && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-3 rounded-b-xl">
          <div className="flex items-center justify-around">
            <ActionButton
              icon={<Eye className="w-4 h-4" />}
              label="View"
              onClick={e => {
                e.stopPropagation();
                onView();
              }}
            />
            <ActionButton
              icon={<Download className="w-4 h-4" />}
              label="Download"
              onClick={e => {
                e.stopPropagation();
                onDownload();
              }}
            />
            <ActionButton
              icon={<Wand2 className="w-4 h-4" />}
              label="Edit"
              onClick={e => {
                e.stopPropagation();
                onEdit();
              }}
            />
            <ActionButton
              icon={<Trash2 className="w-4 h-4" />}
              label="Delete"
              onClick={e => {
                e.stopPropagation();
                onDelete();
              }}
              variant="destructive"
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface IActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  variant?: 'default' | 'destructive';
}

const ActionButton = ({
  icon,
  label,
  onClick,
  variant = 'default',
}: IActionButtonProps) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 p-1.5 rounded-md transition-colors',
        variant === 'destructive'
          ? 'text-white hover:bg-destructive/80 hover:text-white'
          : 'text-white hover:bg-white/20'
      )}
    >
      {icon}
      <span className="text-[10px] font-medium">{label}</span>
    </button>
  );
};
